/*
 * The crew's day, and the pages that come in later builds (each says what it
 * will hold, so the menu already shows the whole app).
 */
import { ClockCard } from "./MyPay";
import { PhoneHome } from "./HomePhone";
import { useMySchedule } from "../api/hooks";
import { useSession } from "../app/session";
import { todayIn } from "../components/format";
import type { IconName } from "../components/Icon";
import { Card, EmptyState, Page } from "../components/parts";

/**
 * The crew's Home (owner, Oct 31: "it should still have a home, schedule and all that for employees"): the owner's phone
 * Home with only their own visits today, the clock on top, no money and no To do. A card opens the crew visit page.
 */
export function MyDay() {
  const { me, company } = useSession();
  const today = todayIn(company.timezone);
  const week = useMySchedule(company.id, { from: today, to: today });
  const now = new Date();
  const hour = Number(
    new Intl.DateTimeFormat("en-US", { timeZone: company.timezone, hour: "numeric", hourCycle: "h23" }).format(now),
  );
  const es = me.locale === "es";
  const hello =
    hour >= 5 && hour < 12
      ? es
        ? "Buenos días"
        : "Good morning"
      : hour >= 12 && hour < 17
        ? es
          ? "Buenas tardes"
          : "Good afternoon"
        : es
          ? "Buenas noches"
          : "Good evening";
  const date = new Intl.DateTimeFormat(es ? "es-US" : "en-US", {
    timeZone: company.timezone,
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(now);
  return (
    <PhoneHome
      crew
      title={`${hello}, ${me.name.split(" ")[0] ?? me.name}`}
      date={date}
      today={today}
      banners={null}
      top={<ClockCard />}
      seesWork
      todays={(week.data?.visits ?? []).filter((v) => v.date === today)}
      loading={!week.data}
      todo={[]}
      health={undefined}
    />
  );
}

export const COMING: Record<string, { title: string; icon: IconName; build: string; what: string[] }> = {
  schedule: {
    title: "Schedule",
    icon: "schedule",
    build: "the next build",
    what: [
      "Day, week and list views",
      "Visits as cards with client, time, address and crew",
      "Drag a visit to move it just that once",
      "Filter by person",
    ],
  },
  quotes: {
    title: "Quotes",
    icon: "quotes",
    build: "build 3",
    what: [
      "Quote builder with optional lines and a deposit",
      "Send it through the Send box after a preview",
      "The client approves and signs online",
      "Turn an approved quote into a job",
    ],
  },
  jobs: {
    title: "Jobs",
    icon: "jobs",
    build: "the next build",
    what: [
      "Every job with its price lines, schedule and crew",
      "Repeats every week, every 2 weeks, monthly or on chosen days",
      "Office notes and crew notes, in Spanish too",
    ],
  },
  invoices: {
    title: "Invoices",
    icon: "invoices",
    build: "build 3",
    what: [
      "To check, Awaiting payment, Past due, Paid",
      "Confirm and send through the Send box",
      "Collect payment by card on file, check or cash",
      "Edit after paid, with the credit question",
    ],
  },
  clients: {
    title: "Clients",
    icon: "clients",
    build: "the next build",
    what: [
      "Search by name or part of a phone number",
      "Client page with properties, jobs, visits, invoices and notes",
      "Card on file and account credit",
      "Merge a duplicate without losing anything",
    ],
  },
  pipeline: {
    title: "Pipeline",
    icon: "pipeline",
    build: "build 3",
    what: ["Leads in stages from new to won", "Where each lead came from", "How long each stage takes"],
  },
  marketing: {
    title: "Marketing",
    icon: "marketing",
    build: "a later build",
    what: ["Reviews, Google profile and campaigns", "Every automation switched off until you turn it on"],
  },
  payments: {
    title: "Payments",
    icon: "payments",
    build: "build 3",
    what: ["Every payment by day, with how it was paid", "Totals for card, bank, cash and check"],
  },
  books: { title: "Books", icon: "books", build: "a later build", what: ["Bank accounts and spending", "Profit by month"] },
  hiring: {
    title: "Hiring",
    icon: "team",
    build: "a later build",
    what: ["Applicants from the careers page", "Notes and next steps per applicant"],
  },
};

export function ComingSoon({ page }: { page: string }) {
  const c = COMING[page];
  if (!c) return null;
  return (
    <Page title={c.title}>
      <Card>
        <EmptyState icon={c.icon} title={`${c.title} comes in ${c.build}`}>
          Here is what this page will do.
        </EmptyState>
        <ul style={{ margin: "0 0 8px", paddingLeft: 40, display: "flex", flexDirection: "column", gap: 6 }}>
          {c.what.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      </Card>
    </Page>
  );
}
